import {
  identifySegmentUser,
  loadSegment,
  resetSegment,
  setSegmentAnonymousId,
  setSegmentConsentState,
  trackSegmentEvent,
  trackSegmentPage,
} from "@/core/analytics/segment/segment.client";
import { getOrCreateVisitorId } from "@/core/identity/visitorId";

import type { AnalyticsInstrumentationSubscriber } from "@/core/analytics/analytics.events";

/**
 * Segment as a consent-gated subscriber, `analytics`-scoped. The sink IS the
 * adapter: it drives the vendor module (`segment.client`) directly, the way
 * the datadog sink drives `monitoring` and the growthbook sink drives
 * `growthbook.runtime` — nothing sits between a sink and its vendor.
 *
 * The sink OWNS loading: `activate` — which the bus runs only once consent
 * resolves with analytics granted — is what loads the SDK, sanitizer
 * middleware included, so before activation Segment does not exist on the
 * page. Deactivation (a revoke, a GPC clamp, engine stop) resets vendor-side
 * identity; the SDK cannot be unloaded, but nothing is routed to it while
 * inactive because the bus delivers nothing to an inactive subscriber.
 *
 * Per-scope semantics: Segment stamps every event's
 * `context.consent.categoryPreferences` with the four scopes, which is how
 * its own consent management filters destinations downstream. The sink feeds
 * it the engine's EFFECTIVE decision on every `consent.changed` — delivered
 * right after activation too, so the first event out already carries the
 * decision that activated the sink.
 *
 * Identity: on activation Segment's anonymous id is set to the first-party
 * visitor id — the id assessments and leads are keyed on (see
 * `AnalyticsProvider.getAnonymousId`) — so Segment's events and the backend's
 * records agree. A revoke resets Segment's copy; a re-grant sets it again.
 * The visitor id itself is never rotated by any of this.
 */
export const createSegmentSink = (): AnalyticsInstrumentationSubscriber => ({
  name: "segment",
  requiredScope: "analytics",
  activate: () => {
    loadSegment();
    const visitorId = getOrCreateVisitorId();
    if (visitorId) {
      setSegmentAnonymousId(visitorId);
    }
  },
  deactivate: () => {
    resetSegment();
  },
  handle: (event) => {
    switch (event.topic) {
      case "consent.changed":
        if (event.payload.status === "resolved") {
          setSegmentConsentState(event.payload.decision);
        }
        return;
      case "analytics.track":
        trackSegmentEvent(event.payload.event, event.payload.properties, event.capturedAt);
        return;
      case "analytics.page":
        trackSegmentPage(event.payload.name, event.capturedAt);
        return;
      case "analytics.identify":
        identifySegmentUser(event.payload.userId, event.payload.traits);
        return;
      case "analytics.reset":
        resetSegment();
        return;
    }
  },
});
